import type {UseQueryResult} from '@tanstack/react-query'
import {useQueryClient} from '@tanstack/react-query'
import {Subjects} from '@freckle/educator-entities/ts/common/helpers/subjects'
import {useAsyncOnceDefined} from '@freckle/educator-entities/ts/common/hooks/use-async'
import type {CourseAttrs} from '@freckle/educator-entities/ts/roster/models/course'
import type {ClaimedFreeResourcesT} from '@freckle/classroom/ts/common/models/claimed-free-resources'
import {fetchClaimedFreeResourcesForCurrentWeek} from '@freckle/classroom/ts/common/models/claimed-free-resources'
import {useTeacher} from './use-teacher'

const QUERY_KEY = 'fetchClaimedFreeResourcesForCurrentWeek' as const

export const useClaimedFreeResourcesForCurrentWeek = (): UseQueryResult<ClaimedFreeResourcesT> => {
  const teacher = useTeacher()
  return useAsyncOnceDefined(
    ({teacher}) => fetchClaimedFreeResourcesForCurrentWeek(teacher.id),
    QUERY_KEY,
    [teacher.data?.id],
    {teacher},
    {staleTime: Infinity},
  )
}

export const useMarkClaimedFreeResourcesStale = (): (() => Promise<void>) => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({queryKey: [QUERY_KEY]})
}

// Ensure that courses (once loaded) have unlimited free resources. This helps
// us ensure we don't show freemium upgrade messages where not relevant (e.g. in
// our chromeless assignment pages). It also removes an unnecessary request for
// claimed free resources.
export const useUnlimitedClaimedFreeResources = (
  coursesResult: UseQueryResult<CourseAttrs[]>,
): UseQueryResult<ClaimedFreeResourcesT> => {
  return useAsyncOnceDefined(
    ({courses}) =>
      Promise.resolve({
        courses: courses.map(c => ({claimedFreeResources: 0, courseId: c.id})),
        maxFreeResourcesPerCourse: Infinity,
        premiumSubjects: Subjects.all(),
      }),
    'useUnlimitedClaimedFreeResources',
    [coursesResult.data?.map(c => c.id)],
    {courses: coursesResult},
  )
}
